import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogFooter from "components/Dialog/DialogFooter.jsx";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import Icon from "@material-ui/core/Icon";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import CustomDatePicker from "components/DatePicker/CustomDatePicker.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";

import api from "api/index";
import ViewToolbar from "components/Toolbar/ViewToolbar.jsx";
import DataTable, { Pill } from "components/DataTable/DataTable.jsx";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const ENTRY_TYPES = ["Lecture", "Tutorial", "Lab", "Practical"];

const TYPE_TONE = {
  Lecture: "info",
  Tutorial: "success",
  Lab: "warning",
  Practical: "danger"
};

const columns = [
  { id: "day", header: "Day", accessorFn: e => e.dayOfWeek || e.day || "—" },
  { id: "time", header: "Time", accessorFn: e => `${e.startTime || "—"} - ${e.endTime || "—"}` },
  {
    id: "course",
    header: "Course",
    accessorFn: e => e.course?.name || "—",
    cell: info => {
      const c = info.row.original.course || {};
      return (
        <div>
          <div style={{ fontWeight: 500, color: "#1e293b" }}>{c.name || "—"}</div>
          {c.code && <div style={{ fontSize: "12px", color: "#64748b" }}>{c.code}</div>}
        </div>
      );
    }
  },
  {
    id: "lecturer",
    header: "Lecturer",
    accessorFn: e => (e.staff ? `${e.staff.firstName || ""} ${e.staff.lastName || ""}`.trim() : "—"),
    cell: info => {
      const s = info.row.original.staff;
      if (!s) return "—";
      return (
        <div>
          <div style={{ fontWeight: 500, color: "#1e293b" }}>{`${s.firstName || ""} ${s.lastName || ""}`.trim()}</div>
          {s.staffNo && <div style={{ fontSize: "12px", color: "#64748b" }}>{s.staffNo}</div>}
        </div>
      );
    }
  },
  { id: "venue", header: "Venue", accessorFn: e => e.venue || "—" },
  {
    id: "type",
    header: "Type",
    cell: info => {
      const type = info.row.original.type || "—";
      return <Pill tone={TYPE_TONE[type] || "muted"}>{type}</Pill>;
    }
  }
];

const BLANK_FORM = {
  day: "Monday",
  startTime: "",
  endTime: "",
  courseId: "",
  staffId: "",
  venue: "",
  type: "Lecture"
};

const styles = {
  container: {
    padding: "30px",
    background: "#f8fafc",
    minHeight: "100vh"
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "32px"
  },
  titleArea: {
    display: "flex",
    flexDirection: "column",
    gap: "8px"
  },
  pageTitle: {
    fontSize: "32px",
    fontWeight: 700,
    color: "#0f172a",
    margin: 0,
    letterSpacing: "-0.02em"
  },
  pageSubtitle: {
    fontSize: "15px",
    color: "#64748b",
    margin: 0
  },
  headerActions: {
    display: "flex",
    gap: "12px"
  },
  
  // KPI Cards
  kpiRow: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "20px",
    marginBottom: "32px"
  },
  kpiCard: {
    background: "#fff",
    padding: "24px",
    borderRadius: "16px",
    border: "1px solid rgba(0,0,0,0.05)",
    boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
    display: "flex",
    gap: "16px",
    alignItems: "flex-start"
  },
  kpiIconBox: {
    width: "48px",
    height: "48px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "24px"
  },
  kpiContent: {
    display: "flex",
    flexDirection: "column",
    gap: "4px"
  },
  kpiValue: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#0f172a",
    lineHeight: 1
  },
  kpiLabel: {
    fontSize: "13px",
    fontWeight: 500,
    color: "#64748b"
  },
  kpiTrend: {
    fontSize: "12px",
    color: "#94a3b8",
    marginTop: "4px"
  },

  // Filters
  filterToolbar: {
    background: "#fff",
    padding: "16px",
    borderRadius: "12px",
    border: "1px solid rgba(0,0,0,0.05)",
    marginBottom: "12px",
    display: "flex",
    flexDirection: "column",
    gap: "16px"
  },
  filterMainRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexWrap: "nowrap"
  },
  searchBox: {
    position: "relative",
    flex: 1,
    minWidth: "200px"
  },
  searchIcon: {
    position: "absolute",
    left: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#64748b",
    fontSize: "20px"
  },
  searchInput: {
    width: "100%",
    padding: "10px 12px 10px 40px",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    fontSize: "14px",
    background: "#fff",
    "&:focus": {
      outline: "none",
      borderColor: "#3b82f6",
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)"
    }
  },
  filterControl: {
    minWidth: "160px"
  },
  filterInputLabel: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#64748b",
    marginBottom: "4px",
    position: "relative",
    transform: "none"
  },
  filterSelect: {
    "& .MuiSelect-select": {
      padding: "10px 12px",
      fontSize: "14px"
    },
    background: "#fff",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    "&::before, &::after": { display: "none" }
  },
  moreFiltersBtn: {
    padding: "10px 16px",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: 500,
    color: "#4f46e5",
    background: "#f5f3ff",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    whiteSpace: "nowrap",
    "&:hover": { background: "#ede9fe" }
  },
  tagRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap"
  },
  filterTag: {
    padding: "4px 12px",
    borderRadius: "999px",
    background: "#eff6ff",
    color: "#2563eb",
    fontSize: "12px",
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    gap: "6px"
  },
  clearAll: {
    fontSize: "12px",
    color: "#64748b",
    cursor: "pointer",
    marginLeft: "8px",
    "&:hover": { color: "#0f172a" }
  },
  toolbarRight: {
    display: "flex",
    alignItems: "center",
    gap: "8px"
  },
  toggleBtnGroup: {
    display: "flex",
    background: "#f1f5f9",
    padding: "2px",
    borderRadius: "8px"
  },
  toggleBtn: {
    padding: "8px 16px",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: 500,
    cursor: "pointer",
    border: "none",
    display: "flex",
    alignItems: "center",
    gap: "6px"
  },
  toggleBtnActive: {
    background: "#fff",
    color: "#0f172a",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
  },
  toggleBtnInactive: {
    background: "transparent",
    color: "#64748b"
  },
  actionBtn: {
    padding: "10px 16px",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    background: "#fff",
    fontSize: "14px",
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    gap: "8px",
    cursor: "pointer"
  },

  errorMsg: { color: "#f44336", fontSize: "13px" },
  formControl: { width: "100%", marginTop: "16px" },
  emptyState: { textAlign: "center", color: "#999", padding: "40px" }
};

class ClassTimetable extends Component {
  state = {
    semesters: [],
    selectedSemester: "",
    selectedCourse: "",
    timetable: [],
    courses: [],
    staff: [],
    loading: false,
    loadingSemesters: true,
    open: false,
    form: { ...BLANK_FORM },
    saving: false,
    error: null,
    formError: null
  };

  componentDidMount() {
    this.fetchSemesters();
    this.fetchCourses();
    this.fetchStaff();
  }

  fetchSemesters = async () => {
    this.setState({ loadingSemesters: true });
    try {
      const res = await api.get("/semesters");
      const d = res.data.data || res.data;
      const list = Array.isArray(d) ? d : d.data || [];
      const current = list.find(s => s.isCurrent) || list[0];
      this.setState(
        { semesters: list, selectedSemester: current ? String(current.id) : "", loadingSemesters: false },
        () => { if (this.state.selectedSemester) this.fetchTimetable(); }
      );
    } catch (e) {
      this.setState({ loadingSemesters: false, error: "Could not load semesters." });
    }
  };

  fetchTimetable = async () => {
    const { selectedSemester, selectedCourse } = this.state;
    if (!selectedSemester) return;
    this.setState({ loading: true, error: null });
    try {
      const res = await api.get("/timetable", {
        params: { semesterId: selectedSemester, courseId: selectedCourse || undefined }
      });
      const d = res.data.data || res.data;
      this.setState({ timetable: Array.isArray(d) ? d : d.data || [], loading: false });
    } catch (e) {
      this.setState({ timetable: [], loading: false, error: "Could not load timetable." });
    }
  };

  fetchCourses = async () => {
    try {
      const res = await api.get("/courses");
      const d = res.data.data || res.data;
      this.setState({ courses: Array.isArray(d) ? d : d.data || [] });
    } catch (e) { /* silent */ }
  };

  fetchStaff = async () => {
    try {
      const res = await api.get("/staff");
      const d = res.data.data || res.data;
      this.setState({ staff: Array.isArray(d) ? d : d.data || [] });
    } catch (e) { /* silent */ }
  };

  handleChange = field => e => {
    const v = e.target.value;
    this.setState(prev => ({ form: { ...prev.form, [field]: v } }));
  };

  handleSemesterChange = e => {
    this.setState({ selectedSemester: e.target.value }, this.fetchTimetable);
  };

  handleCourseFilterChange = e => {
    this.setState({ selectedCourse: e.target.value }, this.fetchTimetable);
  };

  handleSave = async () => {
    const { form, selectedSemester } = this.state;
    if (!form.day || !form.startTime || !form.endTime || !form.courseId) {
      this.setState({ formError: "Day, start time, end time, and course are required." });
      return;
    }
    this.setState({ saving: true, formError: null });
    try {
      await api.post("/timetable", { ...form, semesterId: selectedSemester });
      this.setState({ open: false, saving: false, form: { ...BLANK_FORM } });
      this.fetchTimetable();
    } catch (e) {
      this.setState({ formError: e.response?.data?.message || "Failed to save entry.", saving: false });
    }
  };

  onFilterClick = (show) => {
    // Handled internally by DataTable to toggle toolbar filters
  };

  onSortClick = (event) => {
    // Can be used to show sort options
  };

  onViewClick = (density) => {
    // Density handled internally by DataTable
  };

  render() {
    const { classes } = this.props;
    const {
      semesters, selectedSemester, selectedCourse, courses, staff, timetable,
      loading, loadingSemesters, open, form, saving, error, formError
    } = this.state;

    const uniqueVenues = new Set(timetable.map(e => e.venue).filter(Boolean)).size;
    const currentSem = semesters.find(s => String(s.id) === String(selectedSemester));
    const currentCourse = courses.find(c => String(c.id) === String(selectedCourse));

    return (
      <div className={classes.container}>
        <div className={classes.headerRow}>
          <div className={classes.titleArea}>
            <h1 className={classes.pageTitle}>Class Timetable</h1>
            <p className={classes.pageSubtitle}>Manage and view weekly schedules for all courses and sessions.</p>
          </div>
          <div className={classes.headerActions}>
            <Button color="primary" onClick={() => this.setState({ open: true, form: { ...BLANK_FORM }, formError: null })} style={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, padding: "10px 20px" }}>
              <Icon style={{ marginRight: "8px" }}>add</Icon> Add Timetable Entry
            </Button>
          </div>
        </div>

        <div className={classes.kpiRow}>
          <div className={classes.kpiCard}>
            <div className={`${classes.kpiIconBox}`} style={{ background: "#f5f3ff", color: "#4f46e5" }}>
              <Icon>calendar_today</Icon>
            </div>
            <div className={classes.kpiContent}>
              <span className={classes.kpiValue}>{timetable.length}</span>
              <span className={classes.kpiLabel}>Scheduled Sessions</span>
              <span className={classes.kpiTrend}>Current View</span>
            </div>
          </div>
          <div className={classes.kpiCard}>
            <div className={`${classes.kpiIconBox}`} style={{ background: "#eff6ff", color: "#2563eb" }}>
              <Icon>menu_book</Icon>
            </div>
            <div className={classes.kpiContent}>
              <span className={classes.kpiValue}>{courses.length}</span>
              <span className={classes.kpiLabel}>Total Courses</span>
              <span className={classes.kpiTrend}>Total</span>
            </div>
          </div>
          <div className={classes.kpiCard}>
            <div className={`${classes.kpiIconBox}`} style={{ background: "#f0fdf4", color: "#16a34a" }}>
              <Icon>group</Icon>
            </div>
            <div className={classes.kpiContent}>
              <span className={classes.kpiValue}>{staff.length}</span>
              <span className={classes.kpiLabel}>Active Lecturers</span>
              <span className={classes.kpiTrend}>Total</span>
            </div>
          </div>
          <div className={classes.kpiCard}>
            <div className={`${classes.kpiIconBox}`} style={{ background: "#fffbeb", color: "#d97706" }}>
              <Icon>corporate_fare</Icon>
            </div>
            <div className={classes.kpiContent}>
              <span className={classes.kpiValue}>{uniqueVenues}</span>
              <span className={classes.kpiLabel}>Venues Used</span>
              <span className={classes.kpiTrend}>In this selection</span>
            </div>
          </div>
        </div>

        <div className={classes.filterToolbar}>
          <div className={classes.filterMainRow}>
            <div className={classes.searchBox}>
              <Icon className={classes.searchIcon}>search</Icon>
              <input
                type="text"
                className={classes.searchInput}
                placeholder="Search timetable..."
                value={this.state.searchValue || ""}
                onChange={e => this.setState({ searchValue: e.target.value })}
              />
            </div>

            {loadingSemesters ? (
              <CircularProgress size={20} />
            ) : (
              <FormControl className={classes.filterControl}>
                <InputLabel className={classes.filterInputLabel}>Semester</InputLabel>
                <Select
                  value={selectedSemester}
                  onChange={this.handleSemesterChange}
                  className={classes.filterSelect}
                  disableUnderline
                >
                  <MenuItem value=""><em>— Select Semester —</em></MenuItem>
                  {semesters.map(s => (
                    <MenuItem key={s.id} value={String(s.id)}>{s.name || `Semester ${s.id}`}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <FormControl className={classes.filterControl}>
              <InputLabel className={classes.filterInputLabel}>Filter by Course</InputLabel>
              <Select
                value={selectedCourse}
                onChange={this.handleCourseFilterChange}
                className={classes.filterSelect}
                disableUnderline
              >
                <MenuItem value=""><em>All Courses</em></MenuItem>
                {courses.map(c => (
                  <MenuItem key={c.id} value={String(c.id)}>{c.code ? `${c.code} — ` : ""}{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <button className={classes.moreFiltersBtn}>
              <Icon style={{ fontSize: "20px" }}>filter_list</Icon>
              More Filters
            </button>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div className={classes.tagRow}>
              {currentSem && (
                <div className={classes.filterTag}>
                  {currentSem.name || `Semester ${currentSem.id}`}
                  <Icon style={{ fontSize: "14px", cursor: "pointer" }} onClick={() => this.setState({ selectedSemester: "" }, this.fetchTimetable)}>close</Icon>
                </div>
              )}
              {currentCourse && (
                <div className={classes.filterTag}>
                  {currentCourse.code || currentCourse.name}
                  <Icon style={{ fontSize: "14px", cursor: "pointer" }} onClick={() => this.setState({ selectedCourse: "" }, this.fetchTimetable)}>close</Icon>
                </div>
              )}
              {(selectedSemester || selectedCourse) && (
                <span className={classes.clearAll} onClick={() => this.setState({ selectedSemester: "", selectedCourse: "" }, this.fetchTimetable)}>Clear all</span>
              )}
            </div>

            <div className={classes.toolbarRight}>
              <div className={classes.toggleBtnGroup}>
                <button className={`${classes.toggleBtn} ${classes.toggleBtnActive}`}>
                  <Icon style={{ fontSize: "18px" }}>view_list</Icon> Table View
                </button>
                <button className={`${classes.toggleBtn} ${classes.toggleBtnInactive}`}>
                  <Icon style={{ fontSize: "18px" }}>calendar_today</Icon> Calendar View
                </button>
              </div>
              <button className={classes.actionBtn}>
                <Icon style={{ fontSize: "18px" }}>swap_vert</Icon> Sort
              </button>
              <button className={classes.actionBtn}>
                <Icon style={{ fontSize: "18px" }}>file_upload</Icon> Export <Icon>expand_more</Icon>
              </button>
            </div>
          </div>
        </div>

        {error && <p className={classes.errorMsg}>{error}</p>}

        <DataTable
          columns={columns}
          data={timetable}
          isLoading={loading}
          emptyState="No timetable entries for this selection."
          searchValue={this.state.searchValue}
        />

        {/* Add Entry Dialog */}
        <Dialog open={open} onClose={() => this.setState({ open: false })} maxWidth="sm" fullWidth>
          <DialogHeader title="Add Timetable Entry" />
          <DialogContent>
            {formError && <p className={classes.errorMsg}>{formError}</p>}
            <GridContainer>
              <GridItem xs={12} sm={6}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Day *</InputLabel>
                  <Select value={form.day} onChange={this.handleChange("day")}>
                    {DAYS.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={12} sm={3}>
                <CustomDatePicker
                  dateFormat={false} timeFormat="HH:mm"
                  labelText="Start Time *"
                  id="ttStart"
                  formControlProps={{ fullWidth: true, style: { marginTop: "16px" } }}
                  inputProps={{ value: form.startTime, onChange: this.handleChange("startTime") }}
                />
              </GridItem>
              <GridItem xs={12} sm={3}>
                <CustomDatePicker
                  dateFormat={false} timeFormat="HH:mm"
                  labelText="End Time *"
                  id="ttEnd"
                  formControlProps={{ fullWidth: true, style: { marginTop: "16px" } }}
                  inputProps={{ value: form.endTime, onChange: this.handleChange("endTime") }}
                />
              </GridItem>
              <GridItem xs={12} sm={6}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Course *</InputLabel>
                  <Select value={form.courseId} onChange={this.handleChange("courseId")}>
                    <MenuItem value="">— Select Course —</MenuItem>
                    {courses.map(c => (
                      <MenuItem key={c.id} value={String(c.id)}>
                        {c.code ? `${c.code} — ` : ""}{c.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={12} sm={6}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Lecturer</InputLabel>
                  <Select value={form.staffId} onChange={this.handleChange("staffId")}>
                    <MenuItem value="">— Select Lecturer —</MenuItem>
                    {staff.map(s => (
                      <MenuItem key={s.id} value={String(s.id)}>
                        {`${s.firstName || ""} ${s.lastName || ""}`.trim() || s.staffNo}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={12} sm={6}>
                <CustomInput
                  labelText="Venue"
                  id="ttVenue"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.venue, onChange: this.handleChange("venue"), placeholder: "e.g. Room A1" }}
                />
              </GridItem>
              <GridItem xs={12} sm={6}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Type</InputLabel>
                  <Select value={form.type} onChange={this.handleChange("type")}>
                    {ENTRY_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </Select>
                </FormControl>
              </GridItem>
            </GridContainer>
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ open: false })} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.handleSave} disabled={saving}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Add Entry"}
            </Button>
          </DialogFooter>
        </Dialog>
      </div>
    );
  }
}

ClassTimetable.propTypes = { classes: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(ClassTimetable));
